/**
 * FeedbackInterface (Route: /feedback)
 * -----------------------------------------------------------------------
 * Dedicated "Feedback" (FB) user interface introduced as the fourth UI of
 * the VGWS Care Desk system. Available to every logged-in user — parents,
 * students, staff ticket creators, and admin-panel members — and shows
 * ONLY the feedback-pending tickets that the current user created.
 *
 * Behaviour (per product spec):
 *   - A ticket appears here from the moment its status flips to Resolved.
 *   - Each card lets the creator mark "Satisfied" (thumbs up) or
 *     "Not Satisfied" (thumbs down). Not-Satisfied requires a mandatory
 *     reason (>=10 chars) before Submit is enabled.
 *   - After submission (OR after 24 h, whichever is first), the ticket
 *     automatically disappears from this interface. The backend scheduler
 *     `mark_missed_feedback` closes the window at 24 h and the dedicated
 *     `/api/tickets/pending-feedback` endpoint only returns tickets
 *     inside the active window.
 *   - Analytics continue to track "submitted" vs "missed" counts via
 *     `/api/analytics/overview` (feedback_stats).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, CheckCircle2, Clock, Loader2, MessageSquare, RefreshCw,
  ThumbsDown, ThumbsUp,
} from 'lucide-react';
import { authAPI, ticketAPI } from '@/lib/api';

const FeedbackCard = ({ ticket, onSubmitted }) => {
  const [choice, setChoice] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Derive hours remaining in the 24-hour window so the creator can see
  // how much time is left to respond.
  const hoursRemaining = (() => {
    if (!ticket.resolved_at) return null;
    const resolvedDt = new Date(ticket.resolved_at);
    const deadline = resolvedDt.getTime() + 24 * 3600 * 1000;
    const diffMs = deadline - Date.now();
    return diffMs > 0 ? (diffMs / 3600000).toFixed(1) : 0;
  })();

  const handleSubmit = async () => {
    if (choice === null) {
      toast.error('Please choose Satisfied or Not Satisfied');
      return;
    }
    if (choice === false && reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      await ticketAPI.submitFeedback(ticket.ticket_id, {
        is_satisfactory: choice === true,
        reason: choice === false ? reason.trim() : null,
      });
      toast.success(`Feedback recorded for ticket #${ticket.ticket_id}. Thank you!`);
      onSubmitted?.(ticket.ticket_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`feedback-card-${ticket.ticket_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-base text-[#1e3a5f] flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Ticket #{ticket.ticket_id}
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs ml-1">
                Resolved
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1 text-xs sm:text-sm">
              {ticket.department || 'N/A'}
              {ticket.assigned_staff_name && (
                <> · Resolved by <strong>{ticket.assigned_staff_name}</strong></>
              )}
            </CardDescription>
          </div>
          {hoursRemaining !== null && (
            <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
              <Clock className="w-3 h-3" />
              <span data-testid={`feedback-hours-${ticket.ticket_id}`}>{hoursRemaining}h left</span>
            </div>
          )}
        </div>
        {ticket.description && (
          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{ticket.description}</p>
        )}
      </CardHeader>

      <CardContent className="pt-0 space-y-4">
        <div>
          <Label className="text-sm font-medium">How was your experience? *</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <button
              type="button"
              data-testid={`fb-thumbs-up-${ticket.ticket_id}`}
              onClick={() => setChoice(true)}
              className={`flex flex-col items-center gap-2 border-2 rounded-lg p-4 transition-all ${
                choice === true
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-200 hover:border-emerald-300'
              }`}
            >
              <ThumbsUp className={`w-6 h-6 ${choice === true ? 'text-emerald-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${choice === true ? 'text-emerald-700' : 'text-slate-700'}`}>
                Satisfied
              </span>
            </button>
            <button
              type="button"
              data-testid={`fb-thumbs-down-${ticket.ticket_id}`}
              onClick={() => setChoice(false)}
              className={`flex flex-col items-center gap-2 border-2 rounded-lg p-4 transition-all ${
                choice === false
                  ? 'border-red-500 bg-red-50'
                  : 'border-slate-200 hover:border-red-300'
              }`}
            >
              <ThumbsDown className={`w-6 h-6 ${choice === false ? 'text-red-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${choice === false ? 'text-red-700' : 'text-slate-700'}`}>
                Not Satisfied
              </span>
            </button>
          </div>
        </div>

        {choice === false && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Please mention the reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us what went wrong (minimum 10 characters)"
              data-testid={`fb-reason-${ticket.ticket_id}`}
            />
            <p className="text-xs text-slate-500">{reason.length} characters</p>
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={submitting || choice === null}
          className={`w-full ${
            choice === true
              ? 'bg-emerald-600 hover:bg-emerald-700'
              : choice === false
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-slate-400'
          }`}
          data-testid={`fb-submit-${ticket.ticket_id}`}
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
          ) : choice === true ? (
            <><ThumbsUp className="w-4 h-4 mr-2" /> Submit Feedback</>
          ) : choice === false ? (
            <><ThumbsDown className="w-4 h-4 mr-2" /> Submit Feedback</>
          ) : (
            <>Submit Feedback</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const FeedbackInterface = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [meRes, pendingRes] = await Promise.all([
        authAPI.getMe(),
        ticketAPI.getPendingFeedback(),
      ]);
      setUser(meRes.data);
      setTickets(pendingRes?.data?.tickets || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load feedback data');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('FeedbackInterface load failed:', err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Re-check every 2 min so tickets that cross the 24h mark drop off
    // without requiring a manual refresh.
    pollRef.current = setInterval(loadData, 120000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [loadData]);

  const handleSubmitted = (ticketId) => {
    setTickets((prev) => prev.filter((t) => t.ticket_id !== ticketId));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="feedback-interface">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="fb-back-btn"
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#E97832]" />
                Feedback (FB)
              </h1>
              <p className="text-xs text-slate-500">
                {user?.name ? `Signed in as ${user.name}` : 'Submit feedback on your resolved tickets'}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            data-testid="fb-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 sm:p-4 text-xs sm:text-sm text-amber-900 flex items-start gap-2">
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Feedback window:</strong> 24 hours after the ticket is resolved.
              After that, the feedback card is automatically removed from this page
              regardless of whether you submitted feedback or not. Admin analytics track
              both submissions and missed windows.
            </p>
          </CardContent>
        </Card>

        {loading ? (
          <Card>
            <CardContent className="py-12 flex items-center justify-center text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your feedback list…
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-6 text-sm text-red-800">{error}</CardContent>
          </Card>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm mt-1">
                You have no tickets awaiting feedback. Resolved tickets will appear here
                during their 24-hour feedback window.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-slate-600" data-testid="fb-count">
              <strong>{tickets.length}</strong> ticket{tickets.length === 1 ? '' : 's'} awaiting your feedback
            </p>
            {tickets.map((t) => (
              <FeedbackCard key={t.ticket_id} ticket={t} onSubmitted={handleSubmitted} />
            ))}
          </>
        )}
      </main>
    </div>
  );
};

export default FeedbackInterface;
